import { Controller } from "@hotwired/stimulus"

/**
 * Customize Controller
 * 
 * Manages the customization interface for variants, including toggle switches and collapsible sections.
 * Handles ON/OFF toggles and section header interactions in the customize panel.
 * 
 * Features:
 * - Manages ON/OFF toggle indicators and their visual states
 * - Handles collapsible section headers with chevron rotation
 * - Synchronizes toggle states with section expansion/collapse
 * - Provides visual feedback for active/inactive sections
 * 
 * Usage: Add data-controller="customize" to customization panels with toggle indicators and collapsible sections
 */
export default class extends Controller {
  static targets = ["indicator", "input", "enabled", "disabled"]

  connect() {
    // Initialize legacy jQuery-based customization logic via Stimulus
    if (typeof window.customize_init === "function") {
      window.customize_init()
    }
  }

  initializeToggles() {
    // Initialize all toggle indicators
    this.indicatorTargets.forEach(indicator => {
      this.adjustOnOff(indicator)
    })
  }

  initializeSectionHeaders() {
    // For each customize section header
    document.querySelectorAll('[data-bs-toggle="collapse"]').forEach(header => {
      const chevron = header.querySelector('.chevron-toggle')
      const targetSelector = header.getAttribute('data-bs-target')
      if (!targetSelector) return
      const collapse = document.querySelector(targetSelector)
      if (!collapse) return
      
      // Initial state
      const sectionHeader = header.closest('.customize-section-header')
      if (collapse.classList.contains('show')) {
        if (chevron) chevron.classList.add('chevron-rotated')
        if (sectionHeader) sectionHeader.classList.add('active')
      } else {
        if (chevron) chevron.classList.remove('chevron-rotated')
        if (sectionHeader) sectionHeader.classList.remove('active')
      }
      
      // Listen for collapse events
      collapse.addEventListener('show.bs.collapse', () => {
        if (chevron) chevron.classList.add('chevron-rotated')
        if (sectionHeader) sectionHeader.classList.add('active')
        
        // Set ON/OFF toggle to ON when section is expanded
        const indicatorInput = sectionHeader ? sectionHeader.querySelector('.indicator input') : null
        if (indicatorInput && indicatorInput.value !== 'true') {
          indicatorInput.value = 'true'
          indicatorInput.dispatchEvent(new Event('change', { bubbles: true }))
          
          // Update the visual ON/OFF display
          const indicator = indicatorInput.parentElement
          this.adjustOnOff(indicator)
        }
      })
      
      collapse.addEventListener('hide.bs.collapse', () => {
        if (chevron) chevron.classList.remove('chevron-rotated')
        if (sectionHeader) sectionHeader.classList.remove('active')
      })
    })
  }

  toggle(event) {
    event.preventDefault()
    event.stopPropagation()
    
    const indicator = event.currentTarget
    const input = indicator.querySelector('input')
    const sectionHeader = indicator.closest('.customize-section-header')
    const header = sectionHeader.querySelector('[data-bs-toggle="collapse"]')
    const targetSelector = header.getAttribute('data-bs-target')
    const collapse = targetSelector ? document.querySelector(targetSelector) : null

    if (input.value === "true") {
      // Currently ON - switch to OFF and close collapse
      input.value = "false"
      input.dispatchEvent(new Event('change', { bubbles: true }))
      if (collapse && collapse.classList.contains('show')) {
        header.click()
      }
    } else {
      // Currently OFF - switch to ON
      input.value = "true"
      input.dispatchEvent(new Event('change', { bubbles: true }))
    }
    
    this.adjustOnOff(indicator)
  }

  adjustOnOff(indicator) {
    const input = indicator.querySelector('input')
    
    if (input.value === "true") {
      // ON state - remove 'off' class
      indicator.classList.remove('off')
    } else {
      // OFF state - add 'off' class
      indicator.classList.add('off')
    }
  }
} 